import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Clipboard,
  Settings,
  BarChart3,
  RotateCcw,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  GripVertical,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MessageCircle,
  Eye,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Phase configuration
const PHASE_CONFIG = {
  plan: {
    title: "Plan",
    description: "Establish BCM objectives and processes",
    icon: Clipboard,
    color: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    textColor: "text-blue-700",
  },
  do: {
    title: "Do",
    description: "Implement recovery plans and strategies",
    icon: Settings,
    color: "from-emerald-500 to-emerald-600",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    textColor: "text-emerald-700",
  },
  check: {
    title: "Check",
    description: "Monitor and review BCM performance",
    icon: BarChart3,
    color: "from-amber-500 to-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    textColor: "text-amber-700",
  },
  act: {
    title: "Act",
    description: "Take actions to continually improve",
    icon: RotateCcw,
    color: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    textColor: "text-purple-700",
  },
};

export default function PDCAQuestionsAdminPage() {
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState({
    plan: true,
    do: false,
    check: false,
    act: false,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [isCustom, setIsCustom] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/pdca-questions`);
      const data = await response.json();
      
      if (data.success) {
        setQuestions({
          plan: data.data.plan,
          do: data.data.do,
          check: data.data.check,
          act: data.data.act,
        });
        setIsCustom(data.data.is_custom);
        setLastUpdated(data.data.last_updated);
        setHasChanges(false);
      }
    } catch (error) {
      console.error("Failed to fetch PDCA questions:", error);
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/pdca-questions`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(questions),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success("Questions saved successfully");
        setHasChanges(false);
        setIsCustom(true);
        setLastUpdated(new Date().toISOString());
      } else {
        toast.error(data.detail || "Failed to save questions");
      }
    } catch (error) {
      console.error("Failed to save questions:", error);
      toast.error("Failed to save questions");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/pdca-questions/reset`, {
        method: "POST",
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success("Questions reset to defaults");
        await fetchQuestions();
      } else {
        toast.error(data.detail || "Failed to reset questions");
      }
    } catch (error) {
      console.error("Failed to reset questions:", error);
      toast.error("Failed to reset questions");
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (phase, index, newValue) => {
    setQuestions(prev => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        questions: prev[phase].questions.map((q, i) => (i === index ? newValue : q)),
      },
    }));
    setHasChanges(true);
  };

  const addQuestion = (phase) => {
    setQuestions(prev => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        questions: [...prev[phase].questions, "New question?"],
      },
    }));
    setHasChanges(true);
  };

  const removeQuestion = (phase, index) => {
    setQuestions(prev => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        questions: prev[phase].questions.filter((_, i) => i !== index),
      },
    }));
    setHasChanges(true);
  };

  const moveQuestion = (phase, fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= questions[phase].questions.length) return;
    
    setQuestions(prev => {
      const newQuestions = [...prev[phase].questions];
      const [removed] = newQuestions.splice(fromIndex, 1);
      newQuestions.splice(toIndex, 0, removed);
      
      return {
        ...prev,
        [phase]: {
          ...prev[phase],
          questions: newQuestions,
        },
      };
    });
    setHasChanges(true);
  };

  const updatePhaseInfo = (phase, field, value) => {
    setQuestions(prev => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phase]: !prev[phase],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Guide Questions</h1>
          <p className="text-slate-600 mt-1">
            Customize the pre-populated questions shown in the AI chatbot on the Guide page
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isCustom && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Customized
            </Badge>
          )}
          {lastUpdated && (
            <Badge variant="outline" className="text-slate-500">
              Updated: {new Date(lastUpdated).toLocaleDateString()}
            </Badge>
          )}
        </div>
      </div>

      {/* Action Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <MessageCircle className="w-4 h-4" />
              <span>
                {Object.values(questions || {}).reduce((sum, phase) => sum + (phase.questions?.length || 0), 0)} total questions across 4 phases
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open("/guide", "_blank")}
                data-testid="preview-guide-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Guide
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={saving} data-testid="reset-defaults-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Reset to Defaults
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset to Default Questions?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove all custom questions and restore the original defaults. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleReset} className="bg-red-600 hover:bg-red-700">
                      Reset
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="bg-sky-700 hover:bg-sky-800"
                data-testid="save-questions-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
          {hasChanges && (
            <div className="mt-3 flex items-center gap-2 text-amber-600 text-sm">
              <AlertTriangle className="w-4 h-4" />
              You have unsaved changes
            </div>
          )}
        </CardContent>
      </Card>

      {/* Phase Editors */}
      <div className="space-y-4">
        {Object.entries(PHASE_CONFIG).map(([phaseKey, config]) => {
          const phase = questions?.[phaseKey];
          const IconComponent = config.icon;
          const isExpanded = expandedPhases[phaseKey];

          return (
            <Card key={phaseKey} className={`border-2 ${isExpanded ? config.borderColor : "border-slate-200"}`}>
              {/* Phase Header */}
              <button
                onClick={() => togglePhase(phaseKey)}
                className={`w-full flex items-center justify-between p-4 ${config.bgColor} rounded-t-lg transition-colors`}
                data-testid={`phase-toggle-${phaseKey}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center`}>
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className={`font-semibold ${config.textColor}`}>{config.title}</h3>
                    <p className="text-sm text-slate-500">{phase?.description || config.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={config.textColor}>
                    {phase?.questions?.length || 0} questions
                  </Badge>
                  {isExpanded ? (
                    <ChevronUp className={`w-5 h-5 ${config.textColor}`} />
                  ) : (
                    <ChevronDown className={`w-5 h-5 ${config.textColor}`} />
                  )}
                </div>
              </button>

              {/* Phase Content */}
              {isExpanded && (
                <CardContent className="pt-4">
                  {/* Phase Info */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-4 pb-4 border-b">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Phase Title</label>
                      <Input
                        value={phase?.title || ""}
                        onChange={(e) => updatePhaseInfo(phaseKey, "title", e.target.value)}
                        className="mt-1"
                        data-testid={`phase-title-${phaseKey}`}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Description</label>
                      <Input
                        value={phase?.description || ""}
                        onChange={(e) => updatePhaseInfo(phaseKey, "description", e.target.value)}
                        className="mt-1"
                        data-testid={`phase-description-${phaseKey}`}
                      />
                    </div>
                  </div>

                  {/* Questions List */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Questions</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addQuestion(phaseKey)}
                        data-testid={`add-question-${phaseKey}`}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Question
                      </Button>
                    </div>
                    
                    <ScrollArea className="max-h-[400px]">
                      <div className="space-y-2 pr-2">
                        {phase?.questions?.map((question, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg group"
                          >
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => moveQuestion(phaseKey, index, index - 1)}
                                disabled={index === 0}
                                className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"
                                title="Move up"
                              >
                                <ChevronUp className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => moveQuestion(phaseKey, index, index + 1)}
                                disabled={index === phase.questions.length - 1}
                                className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"
                                title="Move down"
                              >
                                <ChevronDown className="w-3 h-3" />
                              </button>
                            </div>
                            <span className="text-xs text-slate-400 w-6">{index + 1}.</span>
                            <Input
                              value={question}
                              onChange={(e) => updateQuestion(phaseKey, index, e.target.value)}
                              className="flex-1"
                              data-testid={`question-input-${phaseKey}-${index}`}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeQuestion(phaseKey, index)}
                              className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-question-${phaseKey}-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>

                    {(!phase?.questions || phase.questions.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No questions in this phase</p>
                        <Button
                          variant="link"
                          onClick={() => addQuestion(phaseKey)}
                          className="mt-2"
                        >
                          Add your first question
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Tips Card */}
      <Card className="bg-sky-50 border-sky-200">
        <CardContent className="py-4">
          <h4 className="font-medium text-sky-900 mb-2">💡 Tips for Effective Questions</h4>
          <ul className="text-sm text-sky-700 space-y-1">
            <li>• Frame questions from the user's perspective (e.g., "How do I..." rather than "What is...")</li>
            <li>• Cover common pain points and FAQs from your BCM team</li>
            <li>• Keep questions concise but specific enough to get useful AI responses</li>
            <li>• Include questions that guide users to key ResiliencyHUB features</li>
            <li>• Review and update questions based on user feedback</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
